/**
 * Hebrew Mastery ladder — 15 levels based on lifetime translated character
 * count. Ported from the mobile app's stores/progressStore.ts
 * (MASTERY_LEVELS + getMasteryLevel).
 */

export interface MasteryLevelDefinition {
  level: number
  chars: number
  name: string
  nameHe: string
  color: string
  /** apps/web/public/progress/mastery/<image>.webp */
  image: string
}

export const MASTERY_LEVELS: MasteryLevelDefinition[] = [
  { level: 1, chars: 0, name: 'Novice Reader', nameHe: 'קורא מתחיל', color: '#a3e635', image: '1' },
  { level: 2, chars: 5000, name: 'Letter Learner', nameHe: 'לומד אותיות', color: '#22c55e', image: '2' },
  { level: 3, chars: 15000, name: 'Word Wanderer', nameHe: 'נווט מילים', color: '#3b82f6', image: '3' },
  { level: 4, chars: 35000, name: 'Phrase Seeker', nameHe: 'מחפש ביטויים', color: '#e879f9', image: '4' },
  { level: 5, chars: 75000, name: 'Context Grasper', nameHe: 'תופס הקשר', color: '#f97316', image: '5' },
  { level: 6, chars: 150000, name: 'Pattern Spotter', nameHe: 'מזהה דפוסים', color: '#ec4899', image: '6' },
  { level: 7, chars: 300000, name: 'Slang Chaser', nameHe: 'רודף סלנג', color: '#06b6d4', image: '7' },
  { level: 8, chars: 600000, name: 'Cultural Decoder', nameHe: 'פותח תרבות', color: '#eab308', image: '8' },
  { level: 9, chars: 1200000, name: 'Fluent Listener', nameHe: 'מאזין שוטף', color: '#d946ef', image: '9' },
  { level: 10, chars: 2000000, name: 'Street Speaker', nameHe: 'דובר רחוב', color: '#fcd34d', image: '10' },
  { level: 11, chars: 3000000, name: 'Market Master', nameHe: 'אדון השוק', color: '#dc2626', image: '11' },
  { level: 12, chars: 4500000, name: 'Story Teller', nameHe: 'מספר סיפורים', color: '#c026d3', image: '12' },
  { level: 13, chars: 6000000, name: 'Heritage Keeper', nameHe: 'שומר מורשת', color: '#2563eb', image: '13' },
  { level: 14, chars: 8000000, name: 'Language Bridge', nameHe: 'גשר שפות', color: '#ea580c', image: '14' },
  { level: 15, chars: 10000000, name: 'Native By Choice', nameHe: 'ילידי מבחירה', color: '#991b1b', image: '15' },
]

export interface MasteryProgress {
  level: MasteryLevelDefinition
  /** 0-100, rounded. 100 once the last level is reached. */
  progress: number
  /** Characters remaining until the next level, 0 once maxed. */
  charsToNext: number
  nextLevel: MasteryLevelDefinition | null
  totalChars: number
}

/** Current mastery level + progress toward the next, from lifetime characters. */
export function masteryForChars(lifetimeChars: number): MasteryProgress {
  let currentLevel = MASTERY_LEVELS[0]
  let currentIndex = 0

  for (let i = MASTERY_LEVELS.length - 1; i >= 0; i--) {
    if (lifetimeChars >= MASTERY_LEVELS[i].chars) {
      currentLevel = MASTERY_LEVELS[i]
      currentIndex = i
      break
    }
  }

  const nextLevel = MASTERY_LEVELS[currentIndex + 1] ?? null
  let progress = 100
  let charsToNext = 0

  if (nextLevel) {
    const charsInLevel = nextLevel.chars - currentLevel.chars
    const charsProgress = lifetimeChars - currentLevel.chars
    progress = Math.min((charsProgress / charsInLevel) * 100, 100)
    charsToNext = nextLevel.chars - lifetimeChars
  }

  return {
    level: currentLevel,
    progress: Math.round(progress),
    charsToNext: Math.max(0, charsToNext),
    nextLevel,
    totalChars: lifetimeChars,
  }
}

export function masteryImageUrl(level: Pick<MasteryLevelDefinition, 'image'>): string {
  return `/progress/mastery/${level.image}.webp`
}

/**
 * Returns the newly-reached level, if `newChars` crossed into a level that
 * `previousChars` had not reached, else null. Mirrors the mobile app's
 * checkMasteryLevelUp (minus its "already celebrated" storage guard, which
 * store.ts owns).
 */
export function crossedMasteryLevel(newChars: number, previousChars: number): MasteryLevelDefinition | null {
  const currentLevel = masteryForChars(newChars).level
  const previousLevel = masteryForChars(previousChars).level
  return currentLevel.level > previousLevel.level ? currentLevel : null
}
